/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React from "react";
import { Box, useTheme, Modal, Fade, Backdrop, Typography, Stack, Divider, Tooltip, IconButton } from "@mui/material";
import { Loss, ModalBase, Nullable } from "../../utils/Interfaces";
import AttachmentManager from "../../components/general/AttachmentManager";
import { ModelName } from "../../utils/Requests";
import { useWindowDimensions } from "../../utils/Hooks";
import CloseIcon from "@mui/icons-material/Close";

export interface AddLocationModalProps extends ModalBase {
    loss: Nullable<Loss>;
}

const LossDetailsModal = (props: AddLocationModalProps) => {
    const { open, handleClose, loss } = props;
    const theme = useTheme();

    const { width } = useWindowDimensions();
    const mobile = width <= 600;

    if (loss === null) {
        return <Box />;
    }

    return (
        <Box>
            <Modal
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
                open={open}
                onClose={handleClose}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={open}>
                    <Box
                        sx={{
                            backgroundColor: theme.palette.background.paper,
                            border: "1px solid #000",
                            boxShadow: theme.shadows[5],
                            padding: theme.spacing(2, 4, 3),
                            zIndex: 1000,
                            width: mobile ? "100vw" : 600,
                        }}
                    >
                        <Stack direction="row" justifyContent="space-between">
                            <Typography variant="h5" textAlign="center" sx={{ mb: 4 }}>
                                Loss Details
                            </Typography>
                            <Box sx={{ mr: -2, mt: -1 }}>
                                <Tooltip title="Close">
                                    <IconButton onClick={() => handleClose()} size="large">
                                        <CloseIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Typography>Material Name</Typography>
                            <Typography fontWeight="bold">{loss?.name ?? "--"}</Typography>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Typography>Manufacturer</Typography>
                            <Typography fontWeight="bold">{loss?.manufacturer ?? "--"}</Typography>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Typography>Part #</Typography>
                            <Typography fontWeight="bold">{loss?.part_number ?? "--"}</Typography>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Typography>Serial #</Typography>
                            <Typography fontWeight="bold">{loss?.serial_number ?? "--"}</Typography>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Typography>Unit Price</Typography>
                            <Typography fontWeight="bold">${loss?.price ?? "--"}</Typography>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Typography>Reporter</Typography>
                            <Typography fontWeight="bold">
                                {loss?.reporter.first_name} {loss?.reporter.last_name}
                            </Typography>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Typography>Responsible</Typography>
                            <Typography fontWeight="bold">
                                {loss?.responsible.first_name} {loss?.responsible.last_name}
                            </Typography>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Typography>Timestamp</Typography>
                            <Typography fontWeight="bold">
                                {new Date(loss?.date_reported).toLocaleDateString()}{" "}
                                {new Date(loss?.date_reported).toLocaleTimeString()}
                            </Typography>
                        </Stack>

                        {(loss?.notes ?? "") !== "" && (
                            <Box>
                                <Divider sx={{ mt: 3, mb: 2 }}>Notes</Divider>
                                <Typography>{loss?.notes}</Typography>
                            </Box>
                        )}

                        <Divider sx={{ mt: 3, mb: 2 }}>Attachments</Divider>

                        <AttachmentManager model={ModelName.Loss} idKey={loss?.id ?? -1} editing={true} readOnly={false} />
                    </Box>
                </Fade>
            </Modal>
        </Box>
    );
};

export default LossDetailsModal;
